// src/encounter/lib/to-ap-active-medications.ts
// The A&P canvas's ACTIVE MEDICATIONS run, plus the Rx field helpers it shares with the
// pre-encounter merge (`toRichActiveMedications`, services/use-prescription-orders.ts).
//
// DEFECT (Andrew's A&P audit, 2026-09-30): the A&P drew the whole chart list (GetPatientSummary ⋈
// GetPrescriptionOrderDetails) as plan-order rows, so every reported or continuing medication read
// as an order selected in this visit.
//
// FLUTTER: the A&P list is GetPrescriptionOrderDetails ALONE, minus frequency "now"
// (FetchActiveMedicationEvent, patient_encounter_bloc.dart:1683-1700), then minus deleted rows and
// any medication already on the plan, matched by code (getActiveMedicationWithoutNewMedication,
// :3317-3331, read by assessment_plan_screen.dart:1230). The server scopes that read to THIS
// encounter and to Rx rows outside the assessment plan (Scrips.Patient
// EPriscriptionService.GetPrescriptionOrderDetails: `Encounter == encounterId &&
// PatientAssessmentPlanId == null`), so every row it returns was written in this visit.
//
// The chart list itself is unchanged and still shown where it was: the pre-encounter rail's Active
// Medications block and the in-encounter summary banner.
//
// No runtime import from services/use-prescription-orders.ts: the encounter-shell suites mock that
// module wholesale, so anything the page needs from it at runtime has to live here.
import { formatDate } from '@/shared/lib/date-format';
import type { ActiveMedicationItem } from '../encounter-workflow';
import type { PrescriptionOrder } from '../services/use-prescription-orders';
import type { PlanOrder } from './to-patient-assessment';
import type { OrderPayloadMap } from './order-payload';
import { MED_FREQUENCY } from './medication-frequency';

/** Flutter medication status constants (app_constants.dart:362-364, 287-289). */
const STATUS_MAP: Record<string, ActiveMedicationItem['status']> = {
  stop: 'stopped',
  change: 'changed',
  refill: 'refilled',
};

export function text(v: string | null | undefined): string {
  return v?.trim() ?? '';
}

/** Row status from the Rx/summary status, else PRN for frequency "needed", else active. */
export function medStatus(
  status: string | null | undefined,
  frequency: string | null | undefined,
): ActiveMedicationItem['status'] {
  return STATUS_MAP[text(status).toLowerCase()] ?? (text(frequency).toLowerCase() === 'needed' ? 'prn' : 'active');
}

/** "Ordered for" — Flutter getSigAndOrderedForInfo: `${dxCode.displayName} ${dxCode.code}`. */
export function orderedFor(order: PrescriptionOrder): string | undefined {
  const parts = (order.rxDiagnosisCodesRequest ?? [])
    .filter((d) => !d.isDeleted && d.dxCode)
    .map((d) => [text(d.dxCode?.displayName ?? d.dxCode?.display), text(d.dxCode?.code)].filter(Boolean).join(' '))
    .filter(Boolean);
  return parts.length ? parts.join(', ') : undefined;
}

export function quantity(v: number | string | null | undefined): string | undefined {
  if (v === null || v === undefined || v === '') return undefined;
  const n = Number(v);
  return Number.isNaN(n) || n === 0 ? undefined : String(v);
}

export function durationLabel(order: PrescriptionOrder | undefined): string | undefined {
  const duration = text(order?.duration);
  const durationType = text(order?.durationType);
  return duration && durationType ? `${duration} ${durationType.toLowerCase()}` : undefined;
}

/** Codes of the medications on the plan canvas right now (Flutter's `newMeds`). */
function planMedicationCodes(planOrders: readonly PlanOrder[], payloads: OrderPayloadMap): Set<string> {
  const codes = new Set<string>();
  for (const o of planOrders) {
    const p = payloads.get(o.id);
    const code = p?.kind === 'medication' ? text(p.medicationCode.code) : '';
    if (code) codes.add(code);
  }
  return codes;
}

/**
 * This encounter's Rx orders for the A&P ACTIVE MEDICATIONS run (Flutter parity, see the header).
 * Never reads GetPatientSummary: a chart medication must not be drawn as an order in this visit.
 */
export function toApActiveMedications(
  rxOrders: readonly PrescriptionOrder[] | undefined,
  planOrders: readonly PlanOrder[],
  payloads: OrderPayloadMap,
): ActiveMedicationItem[] {
  const onPlan = planMedicationCodes(planOrders, payloads);
  return (rxOrders ?? [])
    .filter((o) => !o.isDeleted && text(o.frequency).toLowerCase() !== MED_FREQUENCY.now)
    .filter((o) => !onPlan.has(text(o.medicationRequest?.code?.code)))
    .map((o, i) => ({
      id: o.id ?? o.orderId ?? `rx-${i}`,
      name: text(o.medicationRequest?.code?.display) || 'Medication',
      sig: text(o.sig) || undefined,
      isRx: true,
      status: medStatus(o.status, o.frequency),
      prescriber: text(o.performerDetails?.name) || undefined,
      orderedOn: formatDate(o.createdDate ?? o.startDate) || undefined,
      orderedFor: orderedFor(o),
      durationLabel: durationLabel(o),
      dispense: quantity(o.dispenseRequest?.quantityValue),
      refills: quantity(o.refillRequest?.quantityValue),
    }));
}
